/**
 * Canvas Rendering
 * 
 * Main UI canvas rendering system for squares, text, and overlays.
 * Background is rendered separately on its own canvas.
 */

import type { GridState } from '../types/square.js';
import type { GridElement } from '../grid-allocation-v2/types.js';
import {
  drawDebugSquareCenters,
  drawGridVerification,
  drawAvoidanceArea,
  type LogoState,
  type LedFlashState,
} from './effects.js';
// Page switcher and backstage controls removed - app now uses single horizontal feed
import { drawDragDropZone, type DragDropZoneState } from './drag-drop-zone.js';
import type { Theme } from '../services/theme/index.js';
import { DEFAULT_BORDER_RADIUS, roundedRect } from './shapes.js';

/**
 * Logo image state
 */
export interface LogoImageState {
  image: HTMLImageElement | null;
  loaded: boolean;
}

/**
 * Create logo image loader
 */
export function createLogoImage(src: string, onLoad?: () => void): LogoImageState {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = src;

  const state: LogoImageState = {
    image: img,
    loaded: false,
  };

  img.onload = () => {
    state.loaded = true;
    // Trigger callback to re-render when logo loads
    if (onLoad) {
      onLoad();
    }
  };

  img.onerror = () => {
  };

  // If image is already loaded (cached), set loaded immediately and trigger callback
  if (img.complete && img.naturalWidth > 0) {
    state.loaded = true;
    if (onLoad) {
      // Use setTimeout to ensure callback runs after current execution context
      setTimeout(() => onLoad(), 0);
    }
  }

  return state;
}

/**
 * Canvas renderer for main UI elements
 * 
 * Renders squares, text, logo, and debug visualization.
 * Background is rendered separately on its own canvas.
 */
export class CanvasRenderer {
  private canvas: HTMLCanvasElement; // Reference to FX canvas (now used for WebGL)
  private offscreenCanvas: HTMLCanvasElement; // Always render 2D to offscreen canvas
  private offscreenCtx: CanvasRenderingContext2D;
  private dpr: number;
  private dirty: boolean;
  // Performance optimizations
  private cachedDpr: number;
  private staticContentDirty: boolean = true;
  private logoCache: ImageBitmap | null = null;
  private textCache: ImageBitmap | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas; // Keep reference but don't use for 2D rendering
    // Always use offscreen canvas for 2D rendering (FX canvas is used for WebGL)
    this.offscreenCanvas = document.createElement('canvas');
    const ctx = this.offscreenCanvas.getContext('2d', {
      willReadFrequently: true, // Optimize for texture reads
      desynchronized: false
    });
    if (!ctx) throw new Error('Failed to get offscreen 2D context');
    this.offscreenCtx = ctx;
    // Enable image smoothing for smooth text rendering
    // We'll filter edge pixels in the shader instead
    this.offscreenCtx.imageSmoothingEnabled = true;
    this.offscreenCtx.imageSmoothingQuality = 'high';

    // Enable text antialiasing and subpixel rendering for crisp text
    // (These are enabled by default, but explicitly setting for clarity)
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.cachedDpr = this.dpr;
    this.dirty = true;
    
    // Update DPR on resize (cache it to avoid recalculation)
    window.addEventListener('resize', () => {
      this.cachedDpr = Math.min(2, window.devicePixelRatio || 1);
      if (this.dpr !== this.cachedDpr) {
        this.dpr = this.cachedDpr;
        this.dirty = true;
      }
    });
  }

  /**
   * Mark canvas as dirty (needs redraw)
   */
  markDirty(): void {
    this.dirty = true;
  }

  /**
   * Mark static content as dirty (logo, text changed)
   */
  markStaticContentDirty(): void {
    this.staticContentDirty = true;
    this.dirty = true;
  }

  /**
   * Get current DPR (cached)
   */
  getDPR(): number {
    return this.dpr;
  }

  /**
   * Render UI elements to offscreen canvas (always, since FX canvas is used for WebGL)
   * Returns the offscreen canvas with the rendered content
   */
  render(
    gridState: GridState,
    elements: GridElement[],
    logoState: LogoImageState,
    logoAnimState: LogoState,
    ledFlashState?: LedFlashState,
    gridVisualization?: 'none' | 'debug' | 'verification' | 'both' | 'avoidance', // Grid visualization mode
    renderToOffscreen: boolean = true, // Always true now
    _getViewForSquare?: (squareId: string) => { metadata?: { showDismissButton?: boolean } } | null, // DEPRECATED: Not used anymore
    getViewCanvases?: () => Array<{ canvas: HTMLCanvasElement; x: number; y: number; width: number; height: number }> | null, // DEPRECATED: No canvas views anymore
    // Page switcher and backstage controls removed - app now uses single horizontal feed
    dragDropZoneState?: DragDropZoneState, // Drag-drop zone state for rendering
    theme?: Theme, // Optional theme for text colors and other rendering properties
    backgroundCanvas?: HTMLCanvasElement | null // Background canvas for frosted glass effect
  ): HTMLCanvasElement {
    // Update offscreen canvas size if needed
    const newWidth = Math.floor(gridState.width * this.dpr);
    const newHeight = Math.floor(gridState.height * this.dpr);

    const sizeChanged = this.offscreenCanvas.width !== newWidth || this.offscreenCanvas.height !== newHeight;
    
    if (sizeChanged) {
      this.offscreenCanvas.width = newWidth;
      this.offscreenCanvas.height = newHeight;
      this.offscreenCtx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.dirty = true; // Mark dirty when size changes
    }

    // PERFORMANCE: Only clear and redraw if dirty or size changed
    // This prevents unnecessary rendering when nothing changed
    if (!this.dirty && !sizeChanged) {
      return this.offscreenCanvas;
    }

    // Clear dirty flag after we've confirmed we need to render
    this.dirty = false;

    // Clear canvas (transparent, background shows through)
    this.offscreenCtx.clearRect(0, 0, gridState.width, gridState.height);

    // NOTE: Square backgrounds are now rendered by each view on its own canvas
    // via BaseView.renderToCanvas(). Views render their own backgrounds, eliminating
    // the split rendering system. This ensures unified rendering and proper layering.

    // Logo and text are now rendered as HTML (removed from canvas)

    // Page switcher and backstage controls rendering removed - app now uses single horizontal feed

    // Draw drag-drop zone (canvas-based, center of screen)
    // HIDDEN: Drag-drop zone is currently hidden as it's unsightly
    // TODO: Consider replacing with microphone module drag-and-drop functionality
    // if (dragDropZoneState) {
    //   drawDragDropZone(this.offscreenCtx, gridState, dragDropZoneState);
    // }

    // Grid visualization orchestration - all modes controlled from here
    // Mode: 'none' | 'debug' | 'verification' | 'both' | 'avoidance'
    const vizMode = gridVisualization || 'none';

    if (vizMode === 'debug' || vizMode === 'both') {
      // Debug visualization: red center dots (grid lines removed)
      drawDebugSquareCenters(this.offscreenCtx, elements);
    }

    if (vizMode === 'verification' || vizMode === 'both' || vizMode === 'avoidance') {
      // Verification visualization: squares with dimensions in center
      drawGridVerification(this.offscreenCtx, gridState);
    }

    if (vizMode === 'avoidance') {
      // Avoidance visualization: shows logo/title bounding box, gutters, and workspace
      drawAvoidanceArea(this.offscreenCtx, gridState, logoState.image, logoState.loaded);
    }

    // Mode 'none' shows nothing (default)

    // PERFORMANCE DEBUG: Skip view compositing in headless mode
    // Import headless flag from base-view
    const HEADLESS_MODE = (window as any).__PIVOT_HEADLESS_MODE === true || false;
    
    // CRITICAL PERFORMANCE FIX: getViewCanvases() callback is called INSIDE render()
    // This means it's called every frame. The callback itself calls getCanvasContent()
    // on all views, which triggers renderToCanvas() if dirty.
    // We MUST ensure this doesn't cause infinite loops or excessive renders.
    // Composite canvas views directly onto offscreen canvas
    // Draw AFTER squares so view content appears on top (correct z-order)
    if (getViewCanvases && !HEADLESS_MODE) {
      // CRITICAL: getViewCanvases() may call getCanvasContent() which triggers renders
      // The callback should be cached by the caller (App class) to prevent repeated calls
      const viewCanvases = getViewCanvases();
      if (viewCanvases && viewCanvases.length > 0) {
        // Viewport culling: only render views that are visible
        const viewportWidth = gridState.width;
        const viewportHeight = gridState.height;
        
        for (const viewCanvas of viewCanvases) {
          // Skip views with invalid dimensions
          if (!viewCanvas.canvas || viewCanvas.width <= 0 || viewCanvas.height <= 0) {
            continue;
          }
          
          // Viewport culling: skip views outside viewport bounds
          const viewRight = viewCanvas.x + viewCanvas.width;
          const viewBottom = viewCanvas.y + viewCanvas.height;
          if (viewRight < 0 || viewCanvas.x > viewportWidth || viewBottom < 0 || viewCanvas.y > viewportHeight) {
            continue;
          }
          
          // PERFORMANCE: Frosted glass effect DISABLED - was causing massive performance issues
          // Creating 2 temporary canvases + blur operation per view per frame was too expensive
          // If needed in future, implement with WebGL shader-based blur (10-100x faster)
          // or cache blurred backgrounds and only recalculate on view position/size changes
          
          // Draw canvas view directly (no frosted glass effect)
          // Note: offscreenCtx is already scaled by DPR, so draw at logical coordinates
          // The view canvas is already scaled by DPR internally
          // Opacity is handled by BaseView.renderToCanvas() - view canvas already has opacity applied
          this.offscreenCtx.drawImage(
            viewCanvas.canvas,
            viewCanvas.x,
            viewCanvas.y,
            viewCanvas.width,
            viewCanvas.height
          );
        }
      }
    }

    return this.offscreenCanvas;
  }
}

